import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import type { GlAccount } from "@/shared/api/accounting";

const columnHelper = createColumnHelper<DeskTableFeatures, GlAccount>();

export type ChartOfAccountsColumnsOpts = {
  companySlug: string;
  busy: boolean;
  onRename: (row: GlAccount, nextName: string) => void;
  onDeactivate: (row: GlAccount) => void;
  onReactivate: (row: GlAccount) => void;
};

export function chartOfAccountsColumns(opts: ChartOfAccountsColumnsOpts) {
  return columnHelper.columns([
    columnHelper.accessor("code", {
      id: "code",
      header: "Code",
      size: 96,
      cell: (info) => (
        <span className="font-mono text-sm">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 240,
      cell: ({ row }) => {
        const a = row.original;
        return (
          <Input
            className="min-w-0 border-transparent hover:border-[var(--color-border)] focus:border-[var(--color-border)]"
            defaultValue={a.name}
            key={`${a.id}-${a.name}`}
            disabled={opts.busy}
            onBlur={(e) => opts.onRename(a, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
            aria-label={`Rename ${a.code}`}
          />
        );
      },
    }),
    columnHelper.accessor("type", {
      id: "type",
      header: "Type",
      size: 120,
      cell: ({ row }) => {
        const a = row.original;
        return (
          <span className="text-xs text-[var(--color-muted)]">
            {a.type}
            {a.isSystem ? " · system" : ""}
            {a.deactivatedAt ? " · hidden" : ""}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 180,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const a = row.original;
        return (
          <div className="flex flex-wrap justify-end gap-1">
            <Button variant="ghost" size="sm" asChild>
              <Link
                to={`/c/${opts.companySlug}/gl/accounts/${a.id}`}
                className="no-underline"
              >
                Ledger
              </Link>
            </Button>
            {a.deactivatedAt ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={opts.busy}
                onClick={() => opts.onReactivate(a)}
              >
                Show
              </Button>
            ) : !a.isSystem ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={opts.busy}
                onClick={() => opts.onDeactivate(a)}
              >
                Hide
              </Button>
            ) : null}
          </div>
        );
      },
    }),
  ]);
}
